/**
 * @file components/audit-tasks/FormSelector.tsx
 * @description 稽核任務的查核表單選擇器（PhasePlanning 內嵌組件）。
 *
 * 功能：
 *  1. 拉取所有可用 form_templates，按案件「方案 + 驗證類型 + 驗證類別」過濾
 *     （2026-09-25 回報序38：改用 @zhcb/shared isFormTemplateApplicable——留空 null/[] 皆為不限、
 *      驗證類別參與過濾；與後端派案自動建表同一套規則）
 *  2. 預先勾選 task 已綁定的（task.forms）
 *  3. 用戶勾選/取消後，「儲存綁定」一次性 diff 提交給後端
 *
 * 後端 POST /api/audit-tasks/:id/forms 是 diff 模式：
 *  - 入參 templateIds 是「希望保留的最終集合」
 *  - 後端比對既有 form_instances 自動新增/軟刪
 *
 * 權限：admin/manager 不限；組長本人可改；其他成員唯讀。
 */

import { Alert, Button, Checkbox, Input, Space, Table, Tag, Typography } from 'antd';
import { message } from '@/lib/antd-app';
import { Save } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { isFormTemplateApplicable, type AuditTaskDetail } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';

interface Props {
  task: AuditTaskDetail;
  onChanged?: () => void;
}

interface FormTemplateItem {
  id: number;
  code: string;
  name: string;
  scheme: string | null;
  applicableSchemes: string[] | null;
  applicableTaskTypes: string[] | null;
  applicableCategories: string[] | null;
  isDefault: boolean;
  isActive: boolean;
  currentVersionId: number | null;
  /** 序179：後端組好的「所在類別+表單序號+表單全名+版本（更新日期）」顯示名稱 */
  displayName: string;
}

interface ListResponse {
  items: FormTemplateItem[];
}

/**
 * 後端 GET /api/form-templates 的 pageSize 上限為 100（Zod schema 限制）。
 * 設置查核表時要一次性看到全部可用模板，故在 queryFn 內按此上限循環翻頁、
 * 直到 totalPages 全部拉完。
 */
const TEMPLATES_PAGE_SIZE = 100;

interface PaginatedTemplates {
  items: FormTemplateItem[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * 判斷表單是否適用於當前任務的案件（序38）。
 * 停用或尚無版本的表單不可選；適用範圍委派共用純函式（null / [] = 不限）。
 */
function isApplicable(tpl: FormTemplateItem, task: AuditTaskDetail): boolean {
  if (!tpl.isActive || tpl.currentVersionId === null) return false;
  return isFormTemplateApplicable(tpl, {
    scheme: task.caseScheme,
    verificationType: task.caseVerificationType,
    verificationCategories: task.caseVerificationCategories ?? null,
  });
}

export function FormSelector({ task, onChanged }: Props) {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const userId = useAuthStore((s) => s.user?.id);
  const isManager = role === 'admin' || role === 'manager';
  // 組長本人可編輯（task.isTeamLead 表示「我這條任務是組長」）
  const canEdit = isManager || (task.isTeamLead && userId === task.assigneeId);

  // 拉所有可用模板：受後端 pageSize=100 上限約束，順序翻頁直到拿完所有頁。
  const { data, isLoading } = useQuery<ListResponse>({
    queryKey: ['form-templates', 'all'],
    queryFn: async () => {
      const all: FormTemplateItem[] = [];
      let page = 1;
      // 第 1 頁先拉，得到 totalPages 後再決定是否繼續；單頁循環避免一次性大查詢
      while (true) {
        const res = await apiClient.get<PaginatedTemplates>(
          `/api/form-templates?page=${page}&pageSize=${TEMPLATES_PAGE_SIZE}`,
        );
        all.push(...res.items);
        if (page >= res.totalPages || res.items.length === 0) break;
        page += 1;
      }
      return { items: all };
    },
  });

  const allTemplates = data?.items ?? [];
  const applicable = useMemo(
    () => allTemplates.filter((t) => isApplicable(t, task)),
    [allTemplates, task],
  );

  // 序124.3：搜索勾選——按 編碼/名稱 關鍵字過濾展示（如「查核表、簽到表、會議記錄表…」）
  const [keyword, setKeyword] = useState('');
  const visible = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return applicable;
    return applicable.filter(
      (t) => t.code.toLowerCase().includes(kw) || t.name.toLowerCase().includes(kw),
    );
  }, [applicable, keyword]);

  // 已綁定的 templateId 集合
  const boundIds = useMemo(() => new Set(task.forms.map((f) => f.templateId)), [task.forms]);

  // 本地勾選狀態
  const [selected, setSelected] = useState<Set<number>>(boundIds);
  // 當外部 task 變化時同步：依賴 boundIds（隨 task.forms 重算）即可。
  useEffect(() => {
    setSelected(new Set(boundIds));
  }, [boundIds]);

  // diff 計算：是否有未保存變更
  const dirty = useMemo(() => {
    if (selected.size !== boundIds.size) return true;
    for (const id of selected) if (!boundIds.has(id)) return true;
    return false;
  }, [selected, boundIds]);

  const saveMutation = useMutation({
    mutationFn: (templateIds: number[]) =>
      apiClient.post<{ added: number[]; removed: number[] }>(`/api/audit-tasks/${task.id}/forms`, {
        templateIds,
      }),
    onSuccess: (res) => {
      const addedCount = res.added.length;
      const removedCount = res.removed.length;
      if (addedCount === 0 && removedCount === 0) {
        message.info('沒有變更');
      } else {
        message.success(`已更新：新增 ${addedCount} 份、解綁 ${removedCount} 份`);
      }
      void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
      onChanged?.();
    },
    onError: (err: { status?: number; message?: string }) => {
      if (err.status === 422) {
        message.error(err.message ?? '部分表單已提交，無法解綁');
      } else if (err.status === 403) {
        message.error('您沒有權限修改任務表單');
      } else {
        message.error('儲存失敗');
      }
    },
  });

  const columns: ColumnsType<FormTemplateItem> = [
    {
      title: '勾選',
      dataIndex: 'id',
      width: 60,
      render: (id: number) => (
        <Checkbox
          checked={selected.has(id)}
          disabled={!canEdit}
          onChange={(e) => {
            const next = new Set(selected);
            if (e.target.checked) next.add(id);
            else next.delete(id);
            setSelected(next);
          }}
        />
      ),
    },
    {
      title: '編碼',
      dataIndex: 'code',
      width: 180,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: '名稱',
      dataIndex: 'displayName',
      ellipsis: true,
    },
    {
      title: '適用方案',
      dataIndex: 'applicableSchemes',
      width: 160,
      render: (schemes: string[] | null, record) => {
        const list = schemes ?? (record.scheme ? [record.scheme] : null);
        return list ? (
          <Space size={4} wrap>
            {list.map((s) => (
              <Tag key={s}>{s}</Tag>
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            通用
          </Typography.Text>
        );
      },
    },
    {
      title: '預設',
      dataIndex: 'isDefault',
      width: 60,
      render: (v: boolean) =>
        v ? <Tag color="gold">默認</Tag> : <Typography.Text type="secondary">—</Typography.Text>,
    },
  ];

  if (!task.caseScheme) {
    return <Alert type="warning" showIcon message="案件缺少驗證方案資訊，無法計算適用表單。" />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
        以下為適用「{task.caseScheme}
        {task.caseVerificationType ? ` · ${task.caseVerificationType}` : ''}
        {task.caseVerificationCategories?.length
          ? ` · ${task.caseVerificationCategories.join('、')}`
          : ''}
        」的查核表清單；管理員可至「系統設置 → 表單管理」維護這份清單與其適用範圍。
      </Typography.Paragraph>

      {/* 序124.2/3：搜索勾選 + 全選 / 反選（作用於當前過濾後的可見清單） */}
      {canEdit && (
        <Space wrap>
          <Input
            allowClear
            size="small"
            style={{ width: 240 }}
            placeholder="搜索表單（查核表、簽到表、會議記錄…）"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          <Button
            size="small"
            onClick={() => {
              const next = new Set(selected);
              visible.forEach((t) => next.add(t.id));
              setSelected(next);
            }}
          >
            全選
          </Button>
          <Button
            size="small"
            onClick={() => {
              const next = new Set(selected);
              visible.forEach((t) => {
                if (next.has(t.id)) next.delete(t.id);
                else next.add(t.id);
              });
              setSelected(next);
            }}
          >
            反選
          </Button>
        </Space>
      )}

      <Table<FormTemplateItem>
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={visible}
        columns={columns}
        pagination={false}
      />

      {canEdit && (
        <Space>
          <Button
            type="primary"
            icon={<Save size={14} />}
            disabled={!dirty}
            loading={saveMutation.isPending}
            onClick={() => saveMutation.mutate(Array.from(selected))}
          >
            {dirty ? '儲存綁定' : '無變更'}
          </Button>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            勾選共 {selected.size} 份；已綁定 {boundIds.size} 份
          </Typography.Text>
        </Space>
      )}

      {!canEdit && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          僅組長 / 管理員可調整任務的查核表組合。
        </Typography.Text>
      )}
    </Space>
  );
}
